import React, { useEffect, useState, useRef } from "react";
import {
  ShieldAlert, Brain, BarChart3, Eye, MessageSquare,
  Sparkles, AlertTriangle, Target, ArrowRight, Info,
  Activity, Fingerprint, Radio
} from "lucide-react";

// ── 8 Threat Vectors (ordered clockwise from top) ──
const THREAT_VECTORS = [
  { key: "political",  short: "Political",       label: "Political Manipulation",       score: 72, icon: Target,       color: "#ef4444", desc: "Content designed to influence political opinion or outcome." },
  { key: "narrative",  short: "Narrative",        label: "Narrative Framing",            score: 69, icon: Sparkles,     color: "#ef4444", desc: "Selective framing to push a particular narrative." },
  { key: "emotional",  short: "Emotional",        label: "Emotional Manipulation",       score: 67, icon: AlertTriangle,color: "#ef4444", desc: "Language triggers strong emotional response." },
  { key: "context",    short: "Context",          label: "Missing Context",              score: 64, icon: Info,         color: "#f59e0b", desc: "Key facts omitted to distort the full picture." },
  { key: "ai_gen",     short: "AI Content",       label: "AI-Generated Content",         score: 63, icon: Brain,        color: "#f59e0b", desc: "Possibility of AI-generated or synthetic media." },
  { key: "clickbait",  short: "Clickbait",        label: "Clickbait & Engagement Traps", score: 61, icon: MessageSquare,color: "#f59e0b", desc: "Sensational headlines or framing to maximize clicks." },
  { key: "stats",      short: "Statistics",       label: "Misleading Statistics",        score: 58, icon: BarChart3,    color: "#f59e0b", desc: "Use of out-of-context or misrepresented data." },
  { key: "source",     short: "Source",           label: "Source Opacity",               score: 55, icon: Eye,          color: "#f59e0b", desc: "Lack of transparency in authorship or funding." },
];

const OVERALL = 64;

function getRiskLabel(score) {
  if (score >= 70) return { text: "High", color: "text-red-400" };
  if (score >= 50) return { text: "Medium", color: "text-amber-400" };
  return { text: "Low", color: "text-emerald-400" };
}

// ── Animated counter ──
function AnimatedNumber({ value, duration = 1200 }) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setDisplay(Math.round(progress * value));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  return <span>{display}</span>;
}

// ── Radial Threat Radar (SVG) ── Bigger viewBox, labels positioned cleanly outside
function ThreatRadar({ vectors, overall }) {
  const cx = 280, cy = 280, maxR = 140;
  const n = vectors.length;
  const angleStep = (2 * Math.PI) / n;

  // Pre-compute positions for each vector
  const positions = vectors.map((v, i) => {
    const angle = angleStep * i - Math.PI / 2; // start from top
    const r = (v.score / 100) * maxR;
    return {
      // Dot on polygon
      dx: cx + Math.cos(angle) * r,
      dy: cy + Math.sin(angle) * r,
      // Label position (well outside the ring)
      lx: cx + Math.cos(angle) * (maxR + 55),
      ly: cy + Math.sin(angle) * (maxR + 55),
      // Score position (strictly below label by 18px)
      sx: cx + Math.cos(angle) * (maxR + 55),
      sy: cy + Math.sin(angle) * (maxR + 55) + 18,
      angle,
      // Text anchor
      anchor: Math.abs(Math.cos(angle)) < 0.15 ? "middle"
            : Math.cos(angle) > 0 ? "start" : "end",
    };
  });

  return (
    <svg viewBox="0 0 560 560" className="w-full h-full select-none" style={{ filter: "drop-shadow(0 0 60px rgba(245, 176, 65, 0.05))" }}>
      {/* Concentric rings */}
      {[0.25, 0.5, 0.75, 1].map((f, i) => (
        <circle key={i} cx={cx} cy={cy} r={maxR * f}
          fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.8"
          strokeDasharray={i < 3 ? "3 5" : "none"} />
      ))}

      {/* Radial spokes */}
      {positions.map((p, i) => (
        <line key={`spoke-${i}`}
          x1={cx} y1={cy}
          x2={cx + Math.cos(p.angle) * maxR}
          y2={cy + Math.sin(p.angle) * maxR}
          stroke="rgba(255,255,255,0.04)" strokeWidth="0.8" />
      ))}

      {/* Filled threat polygon */}
      <polygon
        points={positions.map(p => `${p.dx},${p.dy}`).join(" ")}
        fill="rgba(255, 255, 255, 0.03)"
        stroke="rgba(255, 255, 255, 0.2)"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />

      {/* Dots, labels, scores for each vector */}
      {vectors.map((v, i) => {
        const p = positions[i];
        return (
          <g key={v.key}>
            {/* Pulse ring */}
            <circle cx={p.dx} cy={p.dy} r="6" fill="none" stroke={v.color} strokeWidth="0.8" opacity="0.3">
              <animate attributeName="r" values="4;10;4" dur="3.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.35;0.05;0.35" dur="3.5s" repeatCount="indefinite" />
            </circle>
            {/* Dot */}
            <circle cx={p.dx} cy={p.dy} r="3.5" fill={v.color} stroke="#0a0a0c" strokeWidth="1.5"
              style={{ filter: `drop-shadow(0 0 5px ${v.color})` }} />
            {/* Connector line from ring edge to label */}
            <line
              x1={cx + Math.cos(p.angle) * (maxR + 4)}
              y1={cy + Math.sin(p.angle) * (maxR + 4)}
              x2={cx + Math.cos(p.angle) * (maxR + 38)}
              y2={cy + Math.sin(p.angle) * (maxR + 38)}
              stroke="rgba(255,255,255,0.08)" strokeWidth="0.6" strokeDasharray="2 3" />
            {/* Short label */}
            <text x={p.lx} y={p.ly} textAnchor={p.anchor} dominantBaseline="central"
              fill="#d4d4d8" fontSize="11" fontWeight="500"
              fontFamily="Inter, system-ui, sans-serif" letterSpacing="0.01em">
              {v.short}
            </text>
            {/* Score below label */}
            <text x={p.sx} y={p.sy} textAnchor={p.anchor} dominantBaseline="central"
              fontSize="14" fontWeight="800"
              fontFamily="Inter, system-ui, sans-serif" fill={v.color}>
              {v.score}<tspan fill="#52525b" fontSize="10" fontWeight="500">/100</tspan>
            </text>
          </g>
        );
      })}

      {/* Center hub */}
      <circle cx={cx} cy={cy} r="44" fill="#111114" stroke="rgba(245, 176, 65, 0.25)" strokeWidth="2" />
      {/* Pulse ring on center */}
      <circle cx={cx} cy={cy} r="44" fill="none" stroke="rgba(245, 176, 65, 0.12)" strokeWidth="1">
        <animate attributeName="r" values="44;52;44" dur="4s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.3;0.05;0.3" dur="4s" repeatCount="indefinite" />
      </circle>
      {/* Center score */}
      <text x={cx} y={cy - 4} textAnchor="middle" fill="#f5b041" fontSize="30" fontWeight="900"
        fontFamily="Inter, system-ui, sans-serif">{overall}</text>
      <text x={cx} y={cy + 16} textAnchor="middle" fill="#52525b" fontSize="8" fontWeight="600"
        fontFamily="Inter, system-ui, sans-serif" letterSpacing="0.15em">OVERALL RISK</text>
    </svg>
  );
}

// ── Main Threat Map ──
export default function ThreatMap() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Sort by score descending for the breakdown
  const sorted = [...THREAT_VECTORS].sort((a, b) => b.score - a.score);

  return (
    <section
      ref={sectionRef}
      id="threat-map"
      className="border-t border-ink-border-subtle bg-gradient-to-b from-ink-base via-ink-surface/10 to-ink-base"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-24 md:py-32">

        {/* ── Section Header ── */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
          <div>
            <div className="inline-flex items-center gap-2 text-sm text-brand font-medium tracking-wider uppercase mb-3">
              <Radio size={14} className="animate-pulse" />
              Live Intelligence Preview
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-[1.05]">
              Information Risk Map<span className="font-serif italic font-light text-zinc-400">.</span>
            </h2>
            <p className="mt-3 text-zinc-400 text-lg max-w-2xl leading-relaxed">
              Visualizing potential risks and manipulation patterns in your content.
              Every article gets decomposed across eight intelligence vectors.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-600 border border-ink-border-subtle rounded-full px-4 py-2 bg-ink-surface/40 backdrop-blur shrink-0">
            <Fingerprint size={13} className="text-zinc-500" />
            <span>Sample analysis · for demonstration</span>
          </div>
        </div>

        {/* ── Top Stat Cards ── */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {[
            { label: "OVERALL INFORMATION RISK", value: OVERALL, suffix: "/100", sub: "ELEVATED RISK", subColor: "text-red-400", accent: "border-red-500/20" },
            { label: "MANIPULATION LIKELIHOOD", value: null, text: "High", sub: "Strong indicators detected", subColor: "text-red-400", accent: "border-amber-500/20" },
            { label: "CONFIDENCE LEVEL", value: 82, suffix: "%", sub: "High confidence in assessment", subColor: "text-amber-400", accent: "border-amber-500/20" },
            { label: "SOURCES ANALYZED", value: 14, suffix: "", sub: "Across 7 unique domains", subColor: "text-amber-400", accent: "border-zinc-700/30" },
          ].map((card, i) => (
            <div key={i} className={`rounded-xl border bg-ink-surface/40 backdrop-blur p-5 ${card.accent}`}>
              <div className="text-[10px] text-zinc-500 font-medium tracking-wider uppercase mb-3">
                {card.label}
              </div>
              <div className="text-3xl font-black text-white tracking-tight">
                {card.value !== null ? (
                  <>{visible ? <AnimatedNumber value={card.value} /> : 0}<span className="text-zinc-500 text-lg font-medium">{card.suffix}</span></>
                ) : (
                  <span className="text-red-400">{card.text}</span>
                )}
              </div>
              <div className={`mt-2 text-xs flex items-center gap-1.5 ${card.subColor}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${card.subColor.replace("text-", "bg-")}`} />
                {card.sub}
              </div>
            </div>
          ))}
        </div>

        {/* ── Flow indicator ── */}
        <div className="flex items-center justify-center gap-2 my-6 text-xs text-zinc-600">
          <div className="w-12 h-px bg-gradient-to-r from-transparent to-brand/20" />
          <ArrowRight size={14} className="text-brand/40" />
          <span className="text-zinc-500 tracking-wider uppercase text-[10px] font-medium">Deep vector analysis</span>
          <ArrowRight size={14} className="text-brand/40" />
          <div className="w-12 h-px bg-gradient-to-l from-transparent to-brand/20" />
        </div>

        {/* ── Radar + Breakdown ── */}
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-6 transition-all duration-1000 delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>

          {/* Left: Radar */}
          <div className="lg:col-span-7 rounded-2xl border border-ink-border-subtle bg-ink-surface/30 backdrop-blur p-4 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert size={14} className="text-brand" />
              <span className="text-[10px] text-zinc-400 font-medium tracking-widest uppercase">
                Eight Information Threat Vectors
              </span>
            </div>
            <div className="relative max-w-[520px] mx-auto">
              <ThreatRadar vectors={THREAT_VECTORS} overall={OVERALL} />
            </div>
          </div>

          {/* Right: Threat Breakdown */}
          <div className="lg:col-span-5 rounded-2xl border border-ink-border-subtle bg-ink-surface/30 backdrop-blur p-4 md:p-6 flex flex-col">
            <div className="flex items-center gap-2 mb-5">
              <Activity size={14} className="text-brand" />
              <span className="text-[10px] text-zinc-400 font-medium tracking-widest uppercase">
                Threat Breakdown
              </span>
            </div>

            <div className="space-y-0.5 flex-1">
              {sorted.map((v, i) => {
                const risk = getRiskLabel(v.score);
                const Icon = v.icon;
                return (
                  <div
                    key={v.key}
                    className="group flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-white/[0.02] transition-colors cursor-default"
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border border-white/5"
                      style={{ backgroundColor: `${v.color}15` }}>
                      <Icon size={14} style={{ color: v.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-200 font-medium leading-tight">{v.label}</div>
                      <div className="text-[11px] text-zinc-500 leading-snug mt-0.5 truncate">{v.desc}</div>
                    </div>
                    <div className="text-right shrink-0 pl-2">
                      <div className="text-lg font-black text-white tracking-tight">
                        {v.score}<span className="text-zinc-600 text-xs font-medium">/100</span>
                      </div>
                      <div className={`text-[10px] font-semibold ${risk.color}`}>{risk.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA at bottom */}
            <div className="mt-4 pt-4 border-t border-white/5">
              <button 
                onClick={() => window.location.href = '/app'}
                className="w-full text-center text-sm text-brand hover:text-white border border-brand/20 hover:border-brand/40 rounded-lg px-4 py-2.5 transition-all flex items-center justify-center gap-2 group bg-brand/5 hover:bg-brand/10">
                Analyze your own content
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        </div>

        {/* ── Flow indicator ── */}
        <div className="flex items-center justify-center gap-2 my-8 text-xs text-zinc-600">
          <div className="w-12 h-px bg-gradient-to-r from-transparent to-brand/20" />
          <ArrowRight size={14} className="text-brand/40" />
          <span className="text-zinc-500 tracking-wider uppercase text-[10px] font-medium">Intelligence summary</span>
          <ArrowRight size={14} className="text-brand/40" />
          <div className="w-12 h-px bg-gradient-to-l from-transparent to-brand/20" />
        </div>

        {/* ── Bottom Info Bar ── */}
        <div className={`flex flex-col sm:flex-row gap-4 sm:items-center justify-between rounded-xl border border-ink-border-subtle bg-ink-surface/40 backdrop-blur p-5 transition-all duration-700 delay-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-brand/10 border border-brand/20 flex items-center justify-center shrink-0 mt-0.5">
              <Info size={14} className="text-brand" />
            </div>
            <div>
              <p className="text-sm text-zinc-300 leading-relaxed">
                This Threat Map helps you understand the types of manipulation or risks present in content.
              </p>
              <p className="text-[11px] text-zinc-600 mt-1">
                All scores are based on analysis using multiple specialized models and verified sources.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 sm:max-w-xs shrink-0">
            <div className="w-6 h-6 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center shrink-0 mt-0.5">
              <AlertTriangle size={11} className="text-zinc-400" />
            </div>
            <div>
              <p className="text-xs text-zinc-400">
                <span className="text-zinc-300 font-medium">Remember:</span> High risk doesn't always mean false.
                It means handle with extra awareness.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
